import SparkMD5 from "spark-md5";
import { getFileType, getSequence } from "../parseUtil";
import { parseInt16File, parseWAVfile } from "../AudioFileUtil";
import { INT16, WAV } from "../../pages/app/utils/constants";
import { NON_REACT_FEATURE_FLAGS } from "../../FeatureFlags";

export const FILE_CHUNK_SIZE = 5000 * 1024; // 5MB chunks
export const MAX_WORKERS = 4;
export const MAX_RETRIES = 2;

let isCancelled = false;

export const cancelProcessing = () => {
  isCancelled = true;
};

export const startProcessing = () => {
  isCancelled = false;
};

export const getFileDetails = async (file) => {
  const result = {
    success: true,
    fileData: {},
    error: "",
  };

  const fileType = getFileType(file.path);

  switch (fileType) {
    case WAV: {
      result.fileData = await parseWAVfile(file);
      break;
    }
    case INT16: {
      result.fileData = parseInt16File(file);
      break;
    }
    default: {
      result.success = false;
      result.error = "Invalid File Type: " + file.name;
    }
  }

  return result;
};

export const isDuplicateFile = (file, existingFiles) => {
  return existingFiles.some((existingFile) => existingFile.path === file.path);
};

export const findHighestChannelNumber = (files) => {
  if (files.length === 0) return 0;

  const channelNumbers = files.map((file) =>
    getSequence(file.fileData.fileType, file.name),
  );
  return Math.max(...channelNumbers);
};

export const sortFilesAndAssignChannels = (files) => {
  return files
    .sort((a, b) => a.path.localeCompare(b.path))
    .map((file, index) => ({
      ...file,
      channel: index + 1,
    }));
};

export const validateFileCompatibility = (existingFileData, newFileData) => {
  const errors = [];

  const isWav = (data) => data.fileType === WAV;

  if (!NON_REACT_FEATURE_FLAGS.allowPolyphonicWAV) {
    if (
      isWav(existingFileData) &&
      isWav(newFileData) &&
      existingFileData.channels !== newFileData.channels
    ) {
      errors.push("WAV files: only one multi-channel file allowed");
    }
  }

  if (existingFileData.fileType !== newFileData.fileType) {
    errors.push(`Invalid file type: ${newFileData.fileType}`);
  }

  if (existingFileData.sampleRate !== newFileData.sampleRate) {
    errors.push(`Invalid Sample Rate: ${newFileData.sampleRate}`);
  }

  if (existingFileData.display !== newFileData.display) {
    errors.push(`Invalid Bit Format: ${newFileData.display}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const processFilesForQueue = async (
  fileList,
  existingFiles,
  existingFileData,
) => {
  const result = {
    success: true,
    errors: [],
    warnings: [],
    fileData: { ...existingFileData },
    newFiles: [],
    channelMismatch: false,
  };

  console.log(
    "[processFilesForQueue] Processing files:",
    fileList,
    existingFiles,
  );
  const filesToProcess = Array.from(fileList);

  for (const file of filesToProcess) {
    if (isDuplicateFile(file, [...existingFiles, ...result.newFiles])) {
      result.errors.push(`File is already in list: ${file.name}`);
      continue;
    }

    const fileDetails = await getFileDetails(file);
    if (!fileDetails.success) {
      result.errors.push(fileDetails.error);
      continue;
    }

    if (existingFiles.length === 0 && result.newFiles.length === 0) {
      result.fileData = fileDetails.fileData;
    } else {
      if (result.fileData.fileType === INT16) {
        result.errors.push("INT16 files: only one file allowed");
        continue;
      }

      const compatibility = validateFileCompatibility(
        result.fileData,
        fileDetails.fileData,
      );
      if (!compatibility.isValid) {
        result.errors.push(...compatibility.errors);
        continue;
      }
    }

    // Create file object
    const fileObj = {
      id: generateFileId(file),
      name: file.name,
      path: file.path,
      size: file.size,
      fileObject: file,
      channel: 0, // Will be assigned when sorted
      fileData: fileDetails.fileData,
    };

    result.newFiles.push(fileObj);
  }

  if (result.errors.length > 0) {
    result.success = false;
    return result;
  }

  const allFiles = [...existingFiles, ...result.newFiles];
  const sortedFiles = sortFilesAndAssignChannels(allFiles);

  // For non-INT16 files, check channel count based on filenames
  if (result.fileData.fileType !== INT16) {
    result.fileData.foundChannels = findHighestChannelNumber(sortedFiles);

    result.fileData.numberOfChannels = sortedFiles.reduce(
      (acc, { fileData }) => acc + fileData.channels,
      0,
    );
    if (result.fileData.foundChannels !== result.fileData.numberOfChannels) {
      result.channelMismatch = true;
      result.warnings.push(
        `Channel count mismatch: detected ${result.fileData.numberOfChannels} channels from filenames, but have ${sortedFiles.length} files. You can proceed and the channel count will be updated to match the file count.`,
      );
    }
  } else {
    result.fileData.numberOfChannels = 1;
  }
  return result;
};

export const generateFileId = (file) => {
  return `file_${file.name}_${Math.random().toString(36).substr(2, 9)}`;
};

export const calculateFileHash = (file, bufferSize = FILE_CHUNK_SIZE) => {
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    const hashAlgorithm = new SparkMD5();
    const totalParts = Math.ceil(file.size / bufferSize);
    let currentPart = 0;
    const startTime = Date.now();

    fileReader.onload = function (e) {
      currentPart += 1;
      const buffer = e.target.result;
      const binaryString = arrayBufferToBinaryString(buffer);
      hashAlgorithm.appendBinary(binaryString);

      if (isCancelled) {
        console.warn("Hashing cancelled");
        resolve({
          success: false,
          error: "Hashing cancelled",
        });
        return;
      }
      if (currentPart < totalParts) {
        processNextPart();
        return;
      }

      resolve({
        success: true,
        hash: hashAlgorithm.end(),
        duration: Date.now() - startTime,
      });
    };

    fileReader.onerror = function (e) {
      resolve({
        success: false,
        error: "Failed to read file for hashing",
      });
    };

    function processNextPart() {
      const start = currentPart * bufferSize;
      const end = Math.min(start + bufferSize, file.size);
      fileReader.readAsArrayBuffer(file.slice(start, end));
    }

    processNextPart();
  });
};

function arrayBufferToBinaryString(buffer) {
  const bytes = new Uint8Array(buffer);
  let binaryString = "";
  for (let i = 0; i < bytes.length; i++) {
    binaryString += String.fromCharCode(bytes[i]);
  }
  return binaryString;
}

export const generateFileChunks = (file, chunkSize = FILE_CHUNK_SIZE) => {
  const chunks = [];
  const totalChunks = Math.ceil(file.fileObject.size / chunkSize);

  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.fileObject.size);

    chunks.push({
      id: `${file.id}_chunk_${i}`,
      fileId: file.id,
      fileName: file.name,
      chunkIndex: i,
      start,
      end,
      size: end - start,
      channel: file.channel,
      totalChunks,
    });
  }

  return chunks;
};

export const createChunkFormData = (
  chunk,
  fileObject,
  fileData,
  fileHash,
  comment = "",
) => {
  const formData = new FormData();

  // Get the chunk blob
  const fileSlicer =
    File.prototype.slice ||
    File.prototype.mozSlice ||
    File.prototype.webkitSlice;
  const chunkBlob = fileSlicer.call(fileObject, chunk.start, chunk.end);

  formData.append("fileAudioType", fileData.fileType);
  formData.append("numberOfChannels", fileData.numberOfChannels);
  formData.append("sampleRate", fileData.sampleRate);
  formData.append("bitsPerSample", fileData.bitsPerSample);
  formData.append("float", fileData.float);
  formData.append("comment", comment);
  formData.append("name", chunk.fileName);
  formData.append("md5", fileHash);
  formData.append("start", chunk.start);
  formData.append("chunkSize", chunk.size);
  formData.append("totalFileSize", fileObject.size);
  formData.append("channel", chunk.channel);
  formData.append("file", chunkBlob);

  return formData;
};

export const isUploadQueueValid = (fileData, filesLength) => {
  if (!fileData.fileType || !fileData.sampleRate || filesLength === 0) {
    return false;
  }

  if (fileData.fileType === INT16) {
    return filesLength === 1;
  }

  return true;
};

export const calculateOverallProgress = (progressMap) => {
  const progresses = Object.values(progressMap);
  if (progresses.length === 0) return 0;

  const totalProgress = progresses.reduce(
    (sum, p) => sum + (p.percent || 0),
    0,
  );
  return Math.round(totalProgress / progresses.length);
};

export const getUploadStats = (files, progressMap) => {
  const stats = {
    totalFiles: files.length,
    completedFiles: 0,
    uploadingFiles: 0,
    errorFiles: 0,
    waitingFiles: 0,
    overallProgress: 0,
  };

  files.forEach((file) => {
    const progress = progressMap[file.id];
    if (!progress) {
      stats.waitingFiles++;
    } else {
      switch (progress.status) {
        case "complete":
          stats.completedFiles++;
          break;
        case "uploading":
        case "hashing":
          stats.uploadingFiles++;
          break;
        case "error":
          stats.errorFiles++;
          break;
        default:
          stats.waitingFiles++;
      }
    }
  });

  stats.overallProgress = calculateOverallProgress(progressMap);
  return stats;
};
